import React, { useContext, useRef, useState } from 'react';
import { sparkToast } from '@spark';
import ModelSetup from '@/pages/ModelSetup';
import WizardStepHeading, { WizardNextButton } from './WizardStepHeading';
import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_post } from '@/shared/http/client';
import { applyArmLabelGenerationToProgress } from '@/features/datasets/utils/uploadProgress';
import { formatApiErrorDetail } from '@/shared/utils/formatApiErrorDetail';

/**
 * Wizard Step 3 — "Model configuration" (Figma 3001-68383 and friends).
 * Hosts the shared Model Setup surface (commercial model select, prompts,
 * response mapping, compare toggle) for the created usecase.
 *
 * After prompts are saved, arms deferred LABEL_ONLY generation so AI labeling
 * starts only once the System/User prompts from this step are persisted.
 */
const ModelSetupStep = ({ activeUsecaseId, taskName, onBack, onNext }) => {
    const modelSetupRef = useRef(null);
    const [isAdvancing, setIsAdvancing] = useState(false);
    const { getCurrentUsecase, setProgressStatus } = useContext(GlobalContext);

    if (!activeUsecaseId) return null;

    const armDeferredLabelGeneration = () =>
        new Promise((resolve) => {
            const usecase = getCurrentUsecase?.() || null;
            const clientId = usecase?.client_id;
            if (!clientId || !activeUsecaseId) {
                resolve();
                return;
            }
            data_from_server_post(
                'arm-and-start-label-generation',
                {
                    client_id: clientId,
                    llm_usecase_id: activeUsecaseId,
                },
                (responseJson) => {
                    if (typeof setProgressStatus === 'function') {
                        setProgressStatus((prev) => applyArmLabelGenerationToProgress(prev, {
                            usecaseId: activeUsecaseId,
                            startedDatasets: responseJson?.started_datasets,
                            pendingIngestDatasets: responseJson?.pending_ingest_datasets,
                            alreadyTriggeredDatasets: responseJson?.already_triggered_datasets,
                            skippedNoUnlabeledDatasets: responseJson?.skipped_no_unlabeled_datasets,
                        }));
                    }
                    resolve();
                },
                (err) => {
                    const detail = formatApiErrorDetail(err);
                    console.error('Failed to arm deferred label generation', detail || err);
                    sparkToast.error(
                        detail
                            ? `Could not start label generation: ${detail}`
                            : 'Could not start label generation. It will retry after processing finishes.'
                    );
                    resolve();
                }
            );
        });

    const handleNext = async () => {
        if (isAdvancing) return;
        setIsAdvancing(true);
        try {
            const ok = await modelSetupRef.current?.validateBeforeNext?.();
            if (!ok) return;
            await armDeferredLabelGeneration();
            onNext();
        } finally {
            setIsAdvancing(false);
        }
    };

    return (
        <div className="cw-step cw-step--hosted cw-step--next-last">
            <WizardStepHeading
                title="Model configuration"
                taskName={taskName}
                onBack={onBack}
                onNext={handleNext}
                nextLoading={isAdvancing}
                nextDisableWhenReadOnly
                nextInHeading={false}
            />
            <ModelSetup
                key={activeUsecaseId}
                ref={modelSetupRef}
                embeddedInTaskDrawer
            />
            {/* DOM-last so Tab reaches Next only after the whole step (model
                select, prompts, sample input, JSON keys); the step grid pins
                it visually onto the sticky heading's top-right slot. */}
            <div className="cw-step-heading__next-overlay">
                <WizardNextButton onClick={handleNext} loading={isAdvancing} disableWhenReadOnly />
            </div>
        </div>
    );
};

export default ModelSetupStep;
